import React, { FC, PropsWithChildren } from "react"
import { useParams } from "react-router-dom"
import { Button, Grid, Typography } from "@mui/material"
import {
  HasSidebarLayout,
  DataLoad,
  EntityHeader,
  ProjectInfo,
  ProjectUsers,
  PropsInjector,
  ProjectSensors,
  TabGroupFilter,
  ProjectProductList,
  OuterLink,
  ProjectEventList,
  ClosedEye,
  ProjectReports,
  ProjectTasks,
  ProjectDocumentList,
  ProjectAlertList,
  Status,
  ProjectDataLoggerList,
} from "src/components"
import { SearchParamsKeys } from "src/enums"
import { useProjectRelatedEntitiesAmountRequest, useProjectRequest } from "src/hooks/api"
import { Project, ProjectRelatedEntitiesAmountResponse } from "src/api"
import { config } from "src/config"
import { ProjectWagnerProbeList } from "src/components/sections/project/projectWagnerProbeList"
import { ProjectSections } from "src/components/sections/project/projectSections"
import { ProjectGateways } from "src/components/sections/project/projectGateways"
import { ProjectLoRaWanSensors } from "src/components/sections/project/projectLoRaWanSensors"

const getTabs = ({
  project,
  updateProject,
  amountRelatedEntities,
}: {
  project: Project;
  updateProject: () => void;
  amountRelatedEntities?: ProjectRelatedEntitiesAmountResponse;
}): Status[] => [
  {
    status: "Users",
    extraLabel: String(amountRelatedEntities?.users || 0),
    content: (
      <ProjectUsers
        project={project}
        updateProject={updateProject}
      />
    ),
  },
  {
    status: "Sections",
    extraLabel: String(amountRelatedEntities?.sections || 0),
    content: <ProjectSections projectId={project.id} />,
  },
  {
    status: "Sensors",
    extraLabel: String(amountRelatedEntities?.sensors || 0),
    content: <ProjectSensors projectId={project.id} />,
  },
  {
    status: "Products",
    extraLabel: String(amountRelatedEntities?.products || 0),
    content: <ProjectProductList projectId={project.id} />,
  },
  {
    status: "Wagner Probes",
    extraLabel: String(amountRelatedEntities?.rapidRhProbes || 0),
    content: <ProjectWagnerProbeList projectId={project.id} />,
  },
  {
    status: "Data PUCKs",
    extraLabel: String(amountRelatedEntities?.dataLoggers || 0),
    content: <ProjectDataLoggerList projectId={project.id} />,
  },
  {
    status: "Gateways",
    extraLabel: String(amountRelatedEntities?.gateways || 0),
    content: <ProjectGateways projectId={project.id} />,
  },
  {
    status: "LoRaWan Sensors",
    extraLabel: String(amountRelatedEntities?.lorawanSensors || 0),
    content: <ProjectLoRaWanSensors projectId={project.id} />,
  },
  {
    status: "Events",
    extraLabel: String(amountRelatedEntities?.events || 0),
    content: <ProjectEventList projectId={project.id} />,
  },
  {
    status: "Reports",
    extraLabel: String(amountRelatedEntities?.reports || 0),
    content: <ProjectReports projectId={project.id} />
  },
  {
    status: "Tasks",
    extraLabel: String(amountRelatedEntities?.tasks || 0),
    content: <ProjectTasks projectId={project.id} />,
  },
  {
    status: "Documents",
    extraLabel: String(amountRelatedEntities?.documents || 0),
    content: <ProjectDocumentList projectId={project.id} />,
  },
  {
    status: "Alerts",
    extraLabel: String(amountRelatedEntities?.alerts || 0),
    content: <ProjectAlertList projectId={project.id} />,
  }
]

interface ProjectShowContentProps {
  project?: Project;
  updateProject?: () => void;
  amountRelatedEntities?: ProjectRelatedEntitiesAmountResponse;
}

const ProjectShowContent: FC<ProjectShowContentProps> = ({
  project = null,
  updateProject,
  amountRelatedEntities,
}) => {
  if (!project) return null

  const { name, id, isActive } = project
  document.title = `CLīMIT Admin Panel - Project ${name}`
  const link = `${config.webAppHost}/#/projects/${id}/sections/`

  const getRightIcon = () => {
    if (!isActive) {
      return (
        <Grid container item title="Archived project">
          <ClosedEye />
        </Grid>
      )
    }
    return null
  }

  return (
    <Grid container direction="column" spacing={2}>
      <Grid item>
        <EntityHeader
          title={name}
          rightIcon={isActive ? null : getRightIcon()}
          actionBlockOnTheRight={<OuterLink
            to={link}
            style={{ textDecoration: "none" }}
          >
            <Button variant="contained">Open in web app</Button>
          </OuterLink>}
        />
      </Grid>
      <Grid item container spacing={4} direction="column" wrap="nowrap">
        <Grid item container spacing={2} direction="column" wrap="nowrap">
          <Grid item>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Project Information
            </Typography>
          </Grid>
          <Grid item minWidth={0}>
            <ProjectInfo project={project} />
          </Grid>
        </Grid>
        <Grid item container spacing={2} direction="column">
          <Grid item width="100%">
            <TabGroupFilter
              name={SearchParamsKeys.Tab}
              paramsToRemove={[
                SearchParamsKeys.Page,
                SearchParamsKeys.RowsPerPage,
              ]}
              statusList={getTabs({
                project,
                updateProject: updateProject as () => void,
                amountRelatedEntities,
              })}
            />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  )
}

const ProjectDataLoader: FC<PropsWithChildren<{ projectId: number }>> = ({
  projectId,
  children,
}) => {
  const { isInitialLoading, isError, data, refetch } = useProjectRequest({
    id: projectId,
  })
  const { data: amountRelatedEntities } = useProjectRelatedEntitiesAmountRequest({
    id: projectId,
  })

  const props = {
    project: data || null,
    updateProject: refetch,
    amountRelatedEntities,
  }

  return (
    <DataLoad isLoading={isInitialLoading} isError={isError}>
      <PropsInjector props={props}>{children}</PropsInjector>
    </DataLoad>
  )
}

const ProjectShow: FC = () => {
  const params = useParams()
  const { id } = params as { id: string }

  return (
    <HasSidebarLayout>
      <ProjectDataLoader projectId={parseInt(id, 10)}>
        <ProjectShowContent />
      </ProjectDataLoader>
    </HasSidebarLayout>
  )
}

export default ProjectShow
